import { BaseApi } from "../base.js";
import { API } from "../constants.js";
import router from "@/router";
import { t } from "@/locales/i18n.js";

export class P2PApi extends BaseApi {
  _buildUrl(baseUrl, params) {
    if (!params || Object.keys(params).length === 0) {
      return baseUrl;
    }

    const queryParts = [];

    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) =>
          queryParts.push(`${key}[]=${encodeURIComponent(val)}`),
        );
      } else {
        const encodedValue =
          typeof value === "string"
            ? encodeURIComponent(value).replace(/%2C/g, ",")
            : encodeURIComponent(value);
        queryParts.push(`${key}=${encodedValue}`);
      }
    });

    const queryString = queryParts.join("&");
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  getCurrencies() {
    return this.request(
      () => this.r.get(`${API.EMCD_P2P_2}/p2p/v1/coins`),
      (d) => {
        const { fiat, crypto } = d;
        this.p2p.setFiatCurrencies(fiat);
        this.p2p.setCryptoCurrencies(crypto);
      },
    );
  }

  getPayMethods() {
    return this.request(
      () => this.r.get(`${API.EMCD_P2P_2}/card-details/providers`),
      (d) => this.p2p.setPaymentMethods(d),
    );
  }

  getCardDetailsProviders(fiatCode) {
    const providers = [];
    return this.request(
      () => this.r.get(`${API.EMCD_P2P_2}/card-details/providers`),
      (result) => {
        result.map((item) => {
          if (
            item.currencies.find(
              (element) => element.code === fiatCode.toLowerCase(),
            )
          ) {
            providers.push(item);
          }
        });
      },
    ).then(() => {
      return providers;
    });
  }

  getRates() {
    return this.request(
      () => this.r.get(`${API.ENDPOINT}/rates`),
      (d) => this.p2p.setP2PRates(d.rateMap),
    );
  }

  getOffersList(params) {
    const url = this._buildUrl(`${API.EMCD_P2P_2}/p2p/v1/offer/list`, params);

    return this.request(
      () => this.r.get(url),
      (data) => data,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  getOfferById(id, options = {}) {
    return this.request(
      (opts) => this.r.get(`${API.EMCD_P2P_2}/p2p/v1/offer/${id}`, null, opts),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  getProfile(options = {}) {
    return this.request(
      () => this.r.get(`${API.EMCD_P2P_2}/p2p/v1/profile/my`),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  getProfileById(id, options = {}) {
    return this.request(
      (opts) =>
        this.r.get(`${API.EMCD_P2P_2}/p2p/v1/profile/${id}`, null, opts),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  getRestrictions(options = {}) {
    return this.request(
      () => this.r.get(`${API.EMCD_P2P_2}/p2p/v1/restrictions`),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  getCardDetails(options = {}) {
    return this.request(
      () => this.r.get(`${API.EMCD_P2P_2}/card-details/pm`),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  postCardDetails(payload, options = {}) {
    this.r.set2Fa(this.postCardDetails.bind(this), payload, options);

    return this.request(
      () => this.r.post(`${API.EMCD_P2P_2}/card-details/pm`, payload),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }

      this.ui.addNotification(
        "success",
        t("p2p|my_offers_modal|added_new_payment_method"),
      );
      router.back();
      return res.data;
    });
  }

  postTempCardDetails(payload, options = {}) {
    this.r.set2Fa(this.postTempCardDetails.bind(this), payload, options);

    return this.request(
      () => this.r.post(`${API.EMCD_P2P_2}/card-details/pm/temp`, payload),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      this.ui.addNotification(
        "success",
        t("p2p|my_offers_modal|added_new_payment_method"),
      );
      router.back();
      return res.data;
    });
  }

  deleteCardDetails(id, options = {}) {
    this.r.set2Fa(this.deleteCardDetails.bind(this), id, options);

    return this.request(
      () => this.r.delete(`${API.EMCD_P2P_2}/card-details/pm/${id}`),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }

      this.ui.addNotification(
        "success",
        t("p2p|my_offers_modal|deleted_payment_method"),
      );
      router.back();
      return res.data;
    });
  }

  createOrder(payload, options = {}) {
    return this.request(
      () =>
        this.r.post(`${API.EMCD_P2P_2}/p2p/v1/order/create`, {
          amount_crypto: payload.amount_crypto,
          amount_fiat: payload.amount_fiat,
          offer_id: payload.offer_id,
          payment_method_id: payload.payment_method_id,
        }),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  getHistory(params) {
    const url = this._buildUrl(
      `${API.EMCD_P2P_2}/p2p/v1/order/list/my`,
      params,
    );

    let result;
    return this.request(
      () => this.r.get(url),
      (data) => {
        result = data;
      },
    ).then((res) => result || (res.ok ? res.data : null));
  }

  getOrderById(id, options = {}) {
    return this.request(
      (opts) => this.r.get(`${API.EMCD_P2P_2}/p2p/v1/order/${id}`, null, opts),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  sentFiat(id, options = {}) {
    return this.request(
      () =>
        this.r.post(
          `${API.EMCD_P2P_2}/p2p/v1/order/action/fiat-transfer-sent`,
          { order_id: id },
        ),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  receivedFiat(id, options = {}) {
    this.r.set2Fa(
      async () => {
        await this.receivedFiat(id, options);
      },
      id,
      options,
    );

    return this.request(
      () =>
        this.r.post(
          `${API.EMCD_P2P_2}/p2p/v1/order/action/fiat-transfer-received`,
          { order_id: id },
        ),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      this.ui.addNotification(
        "success",
        t("p2p|order_status|done_description"),
      );
      router.push("/p2p");
      return res.data;
    });
  }

  openDispute(id, options = {}) {
    return this.request(
      () =>
        this.r.post(`${API.EMCD_P2P_2}/p2p/v1/order/action/open-dispute`, {
          order_id: id,
        }),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  getRemainingCancellationCount(options = {}) {
    return this.request(
      () =>
        this.r.get(
          `${API.EMCD_P2P_2}/p2p/v1/profile/remaining-cancellation-count`,
        ),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  cancelOrder(id, options = {}) {
    return this.request(
      () =>
        this.r.post(`${API.EMCD_P2P_2}/p2p/v1/order/action/cancel`, {
          order_id: id,
        }),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  getCommission(payload, options = {}) {
    return this.request(
      () =>
        this.r.post(`${API.EMCD_P2P_2}/p2p/v1/commission`, {
          coin_crypto: payload.coin_crypto,
          direction: payload.direction,
        }),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  uploadMedia(file, options = {}) {
    const formData = new FormData();
    formData.append("file", file);
    return this.request(
      () => this.r.post(`${API.CHAT}/v1/upload`, formData),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  getMediaData(id, options = {}) {
    return this.request(
      () => this.r.post(`${API.CHAT}/v1/header/${id}`),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  chatGetIsClosed(id) {
    return this.request(
      () => this.r.get(`${API.EMCD_P2P_2}/p2p/v1/order/chat/closed/${id}`),
      (data) => data,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data.chat_closed;
    });
  }

  chatMarkAsRead(message_ids) {
    return this.request(
      () =>
        this.r.post(
          `${API.EMCD_P2P_2}/p2p/v1/order/chat/message/mark-as-read`,
          { message_ids },
        ),
      (data) => data,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }

  chatSendMessage(payload, options = {}) {
    const { attachment_ids, order_id, text, type } = payload;
    return this.request(
      () =>
        this.r.post(`${API.EMCD_P2P_2}/p2p/v1/order/chat/message/send`, {
          attachment_ids,
          order_id,
          text,
          type,
        }),
      (data) => data,
      options,
    ).then((res) => {
      if (!res.ok) {
        return Promise.reject(res.code || "Failed to create order");
      }
      return res.data;
    });
  }
}
